import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  unstable_HistoryRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate,
  useSearchParams
} from 'react-router-dom';
import { EventType, InteractionStatus, InteractionType } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal, useMsalAuthentication, AuthenticatedTemplate } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { LandingPage } from './Routes/LandingPage';
import { CandidatePortal } from './Routes/CandidatePortal';
import { Dashboard } from './Routes/CandidatePages/Dashboard';
import { ErrorPage } from './Routes/errorpages/ErrorPage';
// eslint-disable-next-line import/no-cycle
import {
  LandingContent,
  Job,
  Jobs,
  Tifo,
  TimesheetsPage,
  ProfileBuilderOne,
  ProfileBuilderTwo,
  ProfileBuilderThree,
  ProfileBuilderFour,
  ProfileBuilderFive,
  ProfileBuilderSix,
  ProfileBuilderSeven,
  MyProfile,
  ExpensesPage,
  TimesheetPage
} from './Routes';
import { backendAuth, backendUser } from './Api';
import './scss/App.scss';
import RightToBeForgotten from './Routes/rightToBeForgotten/RightToBeForgotten';
import configData from './config.json';
import SEO from './Components/common/SEO/SEO';
import { initUserData } from './actions/user';
import { checkOnboardingCookie } from './utils/helpers';
import api from './api/apiSettings';
import { reactPlugin } from './services/ApplicationInsightsService';
import { getConstants } from './actions/constFromApi';
import { handleLogin } from './utils/loginFunctions';
import AppNotification from './Components/common/AppNotification/AppNotification';
import { loginRequest } from './authConfig';
import { setAllowedParts } from './actions/accessToParts';
import CreateTNEPage from './Routes/CandidatePages/CreateTNEPage/CreateTNEPage';
import { generalEnums, idTokenClaims } from './utils/enums';
import ExpensePage from './Routes/CandidatePages/ExpensePage/ExpensePage';
import InteractionPage from './Routes/InteractionPage/InteractionPage';
import history from './utils/history';

const backendAuthorization = (token, navigate) =>
  backendAuth()
    .then((authResponse) => {
      const {
        status: authStatus,
        data: {
          Data: { FirstName, LastName, Id, IsFirstLogin }
        }
      } = authResponse;
      if (authStatus === 200) {
        localStorage.setItem('AirswiftFirstName', FirstName);
        localStorage.setItem('AirswiftLastName', LastName);
        localStorage.setItem('UserId', Id);
      }
      return { token, Id, navigate, IsFirstLogin };
    })
    .catch((error) => console.log(error));

const getUserInfo = ({ Id, navigate, IsFirstLogin, accounts, dispatch }) =>
  backendUser(Id)
    .then((userResponse) => {
      const {
        status: userStatus,
        data: {
          Data: { IsProfileCompleted }
        }
      } = userResponse;
      const claims = accounts[0]?.idTokenClaims;

      if (userStatus === 200) {
        const jobForSaving = localStorage.getItem('saveJobAfterLogin');
        if (jobForSaving) {
          api.post(`v1/job/save-job`, { JobId: JSON.parse(jobForSaving), UserId: Id });
          localStorage.removeItem('saveJobAfterLogin');
        }
        dispatch(initUserData(userResponse?.data?.Data));
        localStorage.setItem('userProfileCompleted', IsProfileCompleted);

        const page = localStorage.getItem('page');
        const id = localStorage.getItem('pageId');
        const type = localStorage.getItem('type');

        if (page && id && type === 'approve') {
          navigate(`/dashboard/${page}/${id}?approver=true`);
        } else if (page && id) {
          navigate(`/dashboard/${page}/${id}`);
        } else {
          navigate('/dashboard');
        }
        localStorage.removeItem('page');
        localStorage.removeItem('pageId');

        if (
          claims &&
          !claims[idTokenClaims.IS_TNE_APPROVER] &&
          IsFirstLogin &&
          !IsProfileCompleted &&
          !checkOnboardingCookie()
        ) {
          navigate('/dashboard/profile-builder-one');
        }
      }
    })
    .catch((error) => console.log(error));

function App({ msalInstance }) {
  return (
    <Router history={history}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <AppComponent msalInstance={msalInstance} />
      </AppInsightsContext.Provider>
    </Router>
  );
}

function AppComponent({ msalInstance }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance, accounts } = useMsal();
  const [prevProgress, setPrevProgress] = useState(null);
  const [notification, setNotification] = useState(null);
  const appNotification = useSelector((state) => state.appNotifications?.message);
  const accessTo = useSelector((state) => state.accessToParts);
  const accessRef = useRef(accessTo);
  const [searchParams] = useSearchParams();
  const idTokenHint = searchParams.get('id_token_hint');

  const request = {
    scopes: loginRequest.scopes,
    authority: `${configData.REACT_APP_B2C_AUTHORITY}/${generalEnums.MAGICLINKPOLICY}`,
    redirectUri: `/dashboard`,
    extraQueryParameters: {
      id_token_hint: idTokenHint
    }
  };

  if (idTokenHint && !isAuthenticated) {
    useMsalAuthentication(InteractionType.Redirect, request);
  }

  const checkInLS = (param) => {
    const data = localStorage.getItem('restoreAccess');
    if (data) {
      return JSON.parse(data)[param];
    }
    return false;
  };

  const handleRefresh = () => {
    localStorage.setItem('restoreAccess', JSON.stringify(accessTo));
  };

  const restoreData = () => {
    const data = localStorage.getItem('restoreAccess');

    if (data) {
      accessRef.current = data;
      dispatch(setAllowedParts(data));
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleRefresh);
    return () => {
      window.removeEventListener('beforeunload', handleRefresh);
    };
  }, [accessTo]);

  useEffect(() => {
    setNotification(appNotification);
  }, [appNotification]);

  const handleMsalCallback = (message) => {
    if (localStorage.getItem('forRestoreStore')) localStorage.removeItem('forRestoreStore');

    if (message.eventType === EventType.LOGIN_SUCCESS && message.payload.accessToken) {
      const {
        payload: { state, accessToken }
      } = message;
      localStorage.setItem('AirswiftAccessToken', accessToken);
      backendAuthorization(accessToken, navigate).then((res) => getUserInfo({ ...res, accounts, dispatch }));
    }
  };

  const fetchConstants = () => {
    dispatch(
      getConstants(
        (accessTo?.tAndEApprover ||
          accessTo?.tAndEContractor ||
          checkInLS('tAndEContractor') ||
          checkInLS('tAndEApprover')) &&
          isAuthenticated
      )
    );
  };

  useEffect(() => {
    const callbackId = instance.addEventCallback((message) => {
      handleMsalCallback(message);
    });

    if (
      window.location.pathname.toLowerCase().includes('dashboard') &&
      !isAuthenticated &&
      inProgress === InteractionStatus.None &&
      prevProgress === InteractionStatus.Startup &&
      !window.location.hash.includes('access_token')
    ) {
      if (window.location.pathname.includes('/dashboard/')) {
        navigate(window.location.pathname.replace('/dashboard/', '') + window.location.search);
      } else {
        handleLogin(instance);
      }
    }
    setPrevProgress(inProgress);

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [isAuthenticated, inProgress]);

  useEffect(() => {
    const claims = accounts[0]?.idTokenClaims;
    restoreData();
    fetchConstants();
    if (claims) {
      dispatch(
        setAllowedParts({
          onboarding: claims[idTokenClaims.IS_TIFO_USER],
          tAndEContractor: claims[idTokenClaims.IS_TNE_CONTRACTOR],
          tAndEApprover: claims[idTokenClaims.IS_TNE_APPROVER]
        })
      );
    }
    localStorage.removeItem('restoreAccess');
  }, [accounts]);

  return (
    <div className="App">
      <div id="root-portal" />
      <SEO
        title="The best talent deserves the best tech"
        description="Welcome to Airswift Digital, Airswift's self-service portal designed
        to simplify the employment process from start to finish."
        name="Airswift"
        type="website"
        imageUrl={`${configData.REACT_APP_PUBLIC_URL}AirswiftLogo600x600.png`}
        contentUrl={`${configData.REACT_APP_PUBLIC_URL}${window.location.pathname}`}
      />

      <Routes>
        <Route path="/" element={<LandingPage />}>
          <Route index element={<LandingContent />} />
          <Route path="jobs">
            <Route
              path=""
              element={
                <Jobs msalInstance={msalInstance} isLoggedIn={isAuthenticated} navigate={(link) => navigate(link)} />
              }
            />
          </Route>
          <Route path="jobs/:id" element={<Job />} />
        </Route>
        <Route path="/interaction" element={<InteractionPage isLoggedIn={isAuthenticated} />} />
        <Route
          path="my-profile"
          element={
            accessTo?.tAndEApprover ? (
              <Navigate to="/dashboard" />
            ) : (
              <AuthenticatedTemplate>
                <CandidatePortal />
              </AuthenticatedTemplate>
            )
          }
        >
          <Route
            path=""
            element={
              JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <MyProfile />
              ) : (
                <Navigate to="/dashboard/profile-builder-one" />
              )
            }
          />
          <Route path="rtbf" element={<RightToBeForgotten />} />
        </Route>
        <Route
          path="dashboard"
          element={
            <AuthenticatedTemplate>
              <CandidatePortal />
            </AuthenticatedTemplate>
          }
        >
          <Route path="" element={<Dashboard />} />
          <Route path="jobs">
            <Route
              path=""
              element={
                accessTo?.tAndEApprover ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <Jobs msalInstance={msalInstance} isLoggedIn={isAuthenticated} navigate={(link) => navigate(link)} />
                )
              }
            />
          </Route>
          <Route path="jobs/:id" element={<Job />} />
          <Route
            path="tifo"
            element={accessTo?.onboarding || checkInLS('onboarding') ? <Tifo /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="timesheets"
            element={
              accessTo?.tAndEContractor ||
              accessTo?.tAndEApprover ||
              checkInLS('tAndEContractor') ||
              checkInLS('tAndEApprover') ? (
                <TimesheetsPage />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="create-timesheet"
            element={
              accessTo?.tAndEContractor || checkInLS('tAndEContractor') ? (
                <CreateTNEPage type={generalEnums.TIMESHEET} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="create-claim"
            element={
              accessTo?.tAndEContractor || checkInLS('tAndEContractor') ? (
                <CreateTNEPage type={generalEnums.EXPENSE} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="expenses"
            element={
              accessTo?.tAndEContractor ||
              accessTo?.tAndEApprover ||
              checkInLS('tAndEContractor') ||
              checkInLS('tAndEApprover') ? (
                <ExpensesPage />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="timesheet/:id"
            element={
              accessTo?.tAndEContractor ||
              accessTo?.tAndEApprover ||
              checkInLS('tAndEContractor') ||
              checkInLS('tAndEApprover') ? (
                <TimesheetPage />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="expense/:id"
            element={
              accessTo?.tAndEContractor ||
              accessTo?.tAndEApprover ||
              checkInLS('tAndEContractor') ||
              checkInLS('tAndEApprover') ? (
                <ExpensePage />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-one"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderOne />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-two"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderTwo />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-three"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderThree />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-four"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderFour />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-five"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderFive />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-six"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderSix />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route path="profile-builder-seven" element={<ProfileBuilderSeven />} />
        </Route>
        <Route path="/500" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
      {!isAuthenticated && <AppNotification notification={notification} isDrawerOpen={false} />}
    </div>
  );
}

AppComponent.propTypes = {
  msalInstance: PropTypes.any.isRequired
};

export default App;
